// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { AutocompleteInput } from '@shared/ui-kit/components/autocomplete';
import { useForm } from '@advisor-app/hooks';
import { OperationsClientGate } from './operations-client-gate';
import { useSendToTrader } from '../hooks/use-sent-to-trader';
import { useState, useCallback, useMemo } from 'react';
import { AssetOperationModal } from '@shared/portfolio-operations';
import type {
  SourceAsset,
  BuyOperationData,
} from '@shared/portfolio-operations/types';
import { useAppSelector } from '@advisor-app/store/store';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import type { AssetsSearchResp } from '@shared/data-access/interfaces/api/ai/assets';
import { OperationType } from '@shared/data-access/interfaces/operation-type';
import { useTranslation } from 'react-i18next';

const FormAddTickerBuyContent = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetsSearchResp | null>(null);
  const { sendTradeTicker } = useSendToTrader();
  const currency = useAppSelector(selectAccountCurrency);
  const [asset_type, setAssetType] = useState<string | undefined>();
  const { t } = useTranslation(['common']);

  const {
    values: { ticker },
    handleInputChange,
    handleResetForm,
  } = useForm({ ticker: '' });

  const openModal = useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, asset?: AssetsSearchResp) => {
      if (asset) {
        setSelectedAsset(asset);
        setAssetType(asset.asset_type);
        setShowModal(true);
      } else if (ticker) {
        setSelectedAsset({
          ticker: ticker,
          name: ticker,
          asset_type: asset_type || 'stock',
        } as AssetsSearchResp);
        setShowModal(true);
      }
    },
    [ticker, asset_type],
  );

  const closeModal = useCallback(() => {
    handleResetForm();
    setShowModal(false);
    setSelectedAsset(null);
    setAssetType(undefined);
  }, [handleResetForm]);

  const handleBuyConfirm = useCallback(
    (data: BuyOperationData, sourceAsset: SourceAsset) => {
      sendTradeTicker({
        operation: OperationType.BUY,
        ticker: sourceAsset.ticker,
        name: sourceAsset.name,
        volume: data.amount,
        contracts_number: data.shares,
        asset_type: sourceAsset.assetType,
        inputType: data.inputType,
      });
      closeModal();
    },
    [sendTradeTicker, closeModal],
  );

  const sourceAsset: SourceAsset | null = useMemo(() => {
    if (!selectedAsset) return null;

    return {
      ticker: selectedAsset.ticker,
      name: selectedAsset.name || selectedAsset.ticker,
      assetType: selectedAsset.asset_type || asset_type || 'stock',
      investment: 0,
      quantity: 0,
      currency: currency,
      isin: null,
      dfiUrl:
        'dfi_url' in selectedAsset
          ? typeof selectedAsset.dfi_url === 'string' ||
            selectedAsset.dfi_url === null
            ? selectedAsset.dfi_url
            : undefined
          : undefined,
      institution: {
        id: '',
        name: '',
      },
      accountId: '',
    };
  }, [selectedAsset, asset_type, currency]);

  /* istanbul ignore next */
  const onChangeAsset = (value: any) => {
    const assetType = `${value?.asset_type}`;

    return setAssetType(() => {
      if (assetType.includes('stock')) return 'stock';
      if (assetType.includes('etf')) return 'etf';
      if (assetType.includes('mutual_fund')) return 'mutual_fund';

      return undefined;
    });
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
  };

  return (
    <>
      <form className="w-[200px]" onSubmit={handleSubmit}>
        <AutocompleteInput
          onChangeValue={e => handleInputChange(e, { toUpperCase: false })}
          value={ticker}
          onSelectValue={openModal}
          name="ticker"
          className="rounded-full"
          placeholder={t('common.search')}
          error={false}
          leftIcon={<AppIcon icon="Search" color="disabled" size="base" />}
          onChangeDetails={onChangeAsset}
        />
      </form>

      {sourceAsset && (
        <AssetOperationModal
          open={showModal}
          onClose={closeModal}
          operation={OperationType.BUY}
          sourceAsset={sourceAsset}
          onBuy={handleBuyConfirm}
        />
      )}
    </>
  );
};

export const FormAddTickerBuy = () => (
  <OperationsClientGate>
    <FormAddTickerBuyContent />
  </OperationsClientGate>
);
